/**
 * Donner un nom au document quand l'avocat ne lui en a pas donné.
 *
 * Lit les premiers blocs du document d'un scope, en dérive un nom arrêté
 * (`canvasTitreDerive`) et le pose comme titre CHOISI, par le même chemin que
 * le renommage au double-clic. Il part donc dans le PUT du brouillon, se
 * restaure et se laisse écraser par un renommage — sans aucune plomberie
 * nouvelle dans le chemin d'enregistrement.
 *
 * ⚠️ **Jamais pendant qu'un job rédige** : un acte généré tient son nom du
 * titre validé au lancement, et le document est alors en cours de
 * construction. L'appelant filtre ce cas.
 */

import type { SuperDocDocumentApi } from './canvas-agent/superDocDocumentApi';
import { readCanvasSnapshot } from './canvas-agent/canvasSnapshot';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import { renommerLeDocumentDuResident } from './canvasResidentPresentation';
import { titreDeriveDuDocument } from './canvasTitreDerive';

/**
 * Combien de blocs on lit pour trouver un nom.
 *
 * Le nom est dans les tout premiers ; lire au-delà ferait payer une pagination
 * entière à chaque frappe pour une information qu'on a déjà. Huit laisse la
 * place à quelques blocs vides en tête (un DOCX neuf en porte).
 */
const BLOCS_LUS = 8;

/**
 * Pose un nom dérivé sur le document de ce fil, s'il n'en a pas et si le
 * contenu en offre un d'arrêté.
 *
 * @returns le nom posé, ou `null` si rien n'a été posé (aucun contenu arrêté,
 *   lecture impossible, ou montage remplacé pendant la lecture).
 */
export async function nommerLeDocumentSiNecessaire(conversationId: string): Promise<string | null> {
  const scope = canvasDocumentRuntime.getScope(conversationId);
  const instance = scope?.getPhysicalResources().instance ?? null;
  const document = (instance?.activeEditor?.doc as unknown as SuperDocDocumentApi | undefined) ?? null;
  if (!scope || !document) return null;
  // Déjà nommé : ne jamais écraser: le renommage de l'avocat prime sur tout.
  if (scope.getPresentation()?.documentTitle) return null;

  let snapshot;
  try {
    snapshot = await readCanvasSnapshot(document, { offset: 0, limit: BLOCS_LUS });
  } catch {
    // Une lecture qui rejette ne doit jamais remonter : nommer est un confort,
    // pas une opération de l'avocat. On réessaiera à la frappe suivante.
    return null;
  }

  // Même revalidation que `capturerLePlanDuScope` : la lecture est
  // asynchrone, le document a pu être remplacé entre-temps, et poser le nom
  // lu sur le document suivant le baptiserait du nom du précédent.
  if (canvasDocumentRuntime.getScope(conversationId) !== scope) return null;
  if (scope.getPhysicalResources().instance !== instance) return null;
  if (scope.getPresentation()?.documentTitle) return null;

  const titre = titreDeriveDuDocument(snapshot.blocks, snapshot.rawBlocksCount >= BLOCS_LUS);
  if (!titre) return null;

  renommerLeDocumentDuResident({ conversationId, titre });
  // Sans ce marquage, le nom vivrait à l'écran sans jamais atteindre la base :
  // la présentation n'est pas ce qui déclenche l'enregistrement.
  scope.getDraftController().markDirty();
  return titre;
}
